import './SpendAndEffectModal.scss';
import cn from 'classnames';
import { ReactElement, ReactNode, SVGProps, useEffect, useMemo, useState } from 'react';
import { Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import { MdOutlineMenuBook } from 'react-icons/md';
import { TiArrowBack } from 'react-icons/ti';
import { Sankey } from 'recharts';
import { lessThanIgnoreTime } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { formatMoney } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { currencyFormatter, numberFormatter } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { Button } from '@Components/Button';
import { ChartContainer } from '@Components/ChartContainer';
import { DcDgBadge } from '@Components/DcDgBadge/DcDgBadge';
import { Appearance, Design, Placement, PlacementType, Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { WidgetContainer } from '@Components/WidgetContainer';
import { ShadedPattern } from '@Components/FillPatterns/ShadedPattern';
import { getBarChartContributorsData, spendAndEffectDataSort } from '@Pages/MMM/mmm-utils';
import { SpendAndEffectBarChart } from '@Pages/MMM/ModelResults/SpendAndEffectBarChart';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import IncrementalityIcon from '../../assets/IncrementalityIcon.svg?react';
import { Brand60, getColorMap, Neutral300, Neutral400 } from '../../utils/colors';
import {
  SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS,
  SpendAndEffectGlossary
} from './SpendAndEffectGlossary';
import {
  ALL_CHANNELS,
  DEFAULT_SORT_OPTIONS,
  DEMAND_CAP,
  DEMAND_GEN,
  DEMAND_GEN_VS_CAP
} from './spend-and-effect-modal-constants';

const PRIMARY_SANKEY_SET = new Set([
  'activeChannelEffect2',
  'activeChannelEffect',
  'activeChannelSpend',
  'activeChannelSpend2'
]);

const ACTUAL_VALUES = 'Actual Values';
const FULL_BREAKDOWN = '100%';
const DOLLAR_BREAKDOWN = '$1.00';
const COMBINED_DEMAND_GEN_NAME = 'All Combined Demand Generation';
const COMBINED_DEMAND_CAP_NAME = 'All Combined Capture Generation';
const GROUP_BY_SUPPORT_DATE_STR = '2025-03-21T00:00:00';

const DropdownSection = {
  DEFAULTS: 'defaults',
  CHANNELS: 'channels'
} as const;

// Keys of a combined bar-datum that are metadata (not numeric) and therefore must not
// be summed when merging demand-gen / demand-capture rows together.
const NON_NUMERIC_COMBINED_KEYS = new Set([
  'added',
  'brandNonBrandName',
  'channelName',
  'displayName',
  'incrementality',
  'label',
  'label2',
  'name',
  'platformName',
  'tacticName',
  'totalActualizedEffectRawValueLabel',
  'totalActualizedSpendRawValueLabel',
  'totalEffectForChannelLabel',
  'totalSpendForChannelLabel'
]);

interface SankeyNodeRenderProps {
  width: number;
  height: number;
  x: number;
  y: number;
  payload: { name: string };
}

interface SankeyLinkRenderProps {
  sourceX: number;
  targetX: number;
  sourceY: number;
  targetY: number;
  sourceControlX: number;
  targetControlX: number;
  linkWidth: number;
  payload: {
    value: number;
    dy: number;
    source: { name: string; dy: number };
    target: { name: string; dy: number };
  };
}

interface SankeyData {
  nodes: { name: string }[];
  links: { source: number; target: number; value: number }[];
  zeroPrimaryValue: boolean;
}

// A single spend/effect bar datum. Beyond the fixed schema below, each row also carries dynamic
// per-channel contribution keys of the form `${channel}:spend` / `${channel}:effect` (and their
// RawValue variants), which are always numeric — modeled by the pattern index signature.
interface SpendEffectBarDatum {
  name: string;
  displayName: string;
  added: boolean;
  incrementality: boolean;
  contributors: Set<string>;
  channelName: string | null;
  platformName: string | null;
  tacticName: string | null;
  brandNonBrandName: string | null;
  label: string;
  label2: { top: string; bottom: string };
  value: number;
  value2: number;
  spendRawValue: number;
  effectRawValue: number;
  spendOverOutcome: number;
  contributorsSpendSum: number;
  contributorsEffectSum: number;
  contributorsSpendSumRawValue: number;
  contributorsEffectSumRawValue: number;
  totalSpendForChannel: number;
  totalEffectForChannel: number;
  totalActualizedSpend: number;
  totalActualizedSpendRawValue: number;
  totalActualizedEffect: number;
  totalActualizedEffectRawValue: number;
  totalSpendForChannelLabel?: string;
  totalEffectForChannelLabel?: string;
  totalActualizedSpendRawValueLabel?: string;
  totalActualizedEffectRawValueLabel?: string;
  [contributionKey: `${string}:${string}`]: number;
}

interface SpendAndEffectModalProps {
  onClose: () => void;
  data: SpendEffectBarDatum[];
  valueFormatter: (value: number) => string;
  sortOptions: { value: string }[];
  kpiType: string;
  excelDownload: () => void;
  groupBy: string;
  setGroupBy: (groupBy: string) => void;
  groupByOptions: { label: string; value: string }[];
  sortDropdownValue: string;
  setSortDropdownValue: (value: string) => void;
  modelDate: string;
}

// Builds a single "combined" bar datum by summing every numeric field of the source rows
// and unioning their contributor sets. Metadata keys are copied from the seed, not summed.
const buildCombinedBarData = (
  source: SpendEffectBarDatum[],
  name: string,
  added: boolean
): SpendEffectBarDatum[] => {
  let combinedBarData: SpendEffectBarDatum = {
    // Default "empty" values
    channelName: null,
    platformName: null,
    tacticName: null,
    brandNonBrandName: null,
    incrementality: false,
    contributors: new Set<string>(),
    contributorsSpendSum: 0,
    contributorsEffectSum: 0,
    totalSpendForChannel: 0,
    totalEffectForChannel: 0,
    value: 0,
    value2: 0,
    label: '',
    label2: {
      top: '',
      bottom: ''
    },
    spendOverOutcome: 0,
    spendRawValue: 0,
    effectRawValue: 0,
    contributorsSpendSumRawValue: 0,
    contributorsEffectSumRawValue: 0,
    totalActualizedSpend: 0,
    totalActualizedSpendRawValue: 0,
    totalActualizedEffect: 0,
    totalActualizedEffectRawValue: 0,
    // Input values
    name,
    displayName: name,
    added
  };

  // Everything left after the metadata/contributor keys is numeric, but the keys are dynamic
  // (per-channel contribution keys), so view the row and accumulator as numeric maps for the sum.
  const numericAcc = combinedBarData as unknown as Record<string, number>;

  source.forEach(row => {
    const numericRow = row as unknown as Record<string, number>;
    Object.keys(row).forEach(key => {
      if (NON_NUMERIC_COMBINED_KEYS.has(key)) {
        return;
      }

      // Combine contributor sets
      if (key === 'contributors') {
        combinedBarData.contributors = new Set<string>([
          ...Array.from<string>(row.contributors),
          ...Array.from<string>(combinedBarData.contributors)
        ]);
        return;
      }

      // Sum any numeric key
      numericAcc[key] = (numericAcc[key] ?? 0) + numericRow[key];
    });
  });

  combinedBarData = {
    ...combinedBarData,
    totalSpendForChannelLabel: currencyFormatter.format(combinedBarData.totalSpendForChannel),
    totalEffectForChannelLabel: currencyFormatter.format(combinedBarData.totalEffectForChannel),
    totalActualizedSpendRawValueLabel: currencyFormatter.format(
      combinedBarData.totalActualizedSpendRawValue
    ),
    totalActualizedEffectRawValueLabel: currencyFormatter.format(
      combinedBarData.totalActualizedEffectRawValue
    )
  };

  return [combinedBarData];
};

// Formats the value shown on a sankey link, depending on the selected breakdown mode.
const formatSankeyLinkLabel = (
  breakdown: string,
  name: string,
  kpiType: string,
  value: number,
  ratio: number
): string => {
  if (breakdown === ACTUAL_VALUES) {
    if (name === 'Effect' && kpiType !== 'revenue') {
      return numberFormatter.format(value);
    }
    return currencyFormatter.format(value);
  }
  if (breakdown === DOLLAR_BREAKDOWN) {
    return formatMoney(ratio);
  }
  return `${(ratio * 100).toFixed(2)}%`;
};

const mapBottomPlacement = (align: 'center' | 'start' | 'end'): PlacementType => {
  if (align === 'start') return Placement.BOTTOM_START;
  if (align === 'end') return Placement.BOTTOM_END;
  return Placement.BOTTOM;
};

interface SankeyRenderContext {
  isValueAdded: boolean;
  colorMap: Record<string, string>;
  activeChannel: string;
  kpiType: string;
}

// Factory for the recharts Sankey `node` renderer. Defined at module scope (rather than inside
// the component) so it is not treated as an unstable nested component.
const renderSankeyNode = ({ isValueAdded, colorMap, activeChannel }: SankeyRenderContext) =>
  function SankeyNode({ width, height, payload, x, y }: SankeyNodeRenderProps): ReactElement {
    const shading = isValueAdded
      ? `url(#shadedStripeNeg${colorMap[payload.name]})`
      : `url(#shadedStripe${colorMap[payload.name]})`;
    const resolvedFill = PRIMARY_SANKEY_SET.has(payload.name) ? colorMap[activeChannel] : shading;
    const outlineNode = isValueAdded
      ? payload.name === 'activeChannelEffect' || payload.name === 'activeChannelSpend'
      : payload.name === 'activeChannelEffect2' || payload.name === 'activeChannelSpend2';
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={resolvedFill}
        // No design-system palette equivalent for this chart-node outline grey.
        stroke={outlineNode ? '#4e4e50' : undefined}
        strokeWidth={2}
        ry={outlineNode ? 2 : undefined}
      />
    );
  };

// Factory for the recharts Sankey `link` renderer, bound to a given series name and breakdown.
const renderSankeyLink = (
  { isValueAdded, colorMap, activeChannel, kpiType }: SankeyRenderContext,
  name: string,
  breakdown: string
) =>
  function SankeyLink({
    sourceX,
    targetX,
    sourceY,
    targetY,
    sourceControlX,
    targetControlX,
    linkWidth,
    payload
  }: SankeyLinkRenderProps): ReactElement<SVGProps<SVGPathElement>> {
    const isPrimary = isValueAdded
      ? PRIMARY_SANKEY_SET.has(payload.source.name)
      : PRIMARY_SANKEY_SET.has(payload.target.name);
    const resolvedFill = isPrimary
      ? colorMap[activeChannel]
      : colorMap[isValueAdded ? payload.source.name : payload.target.name];
    const resolvePathd = `M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`;
    const ratio = isValueAdded ? payload.dy / payload.target.dy : payload.dy / payload.source.dy;
    const prettyLabel = formatSankeyLinkLabel(breakdown, name, kpiType, payload.value, ratio);

    const primaryDefinition =
      name === 'Spend'
        ? SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS['Direct Spend']
        : SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS['Direct Effect'];

    return (
      <g>
        <path
          d={resolvePathd}
          fill='none'
          stroke={resolvedFill}
          strokeOpacity={0.4}
          strokeWidth={linkWidth}
          strokeLinecap='butt'
        />
        <foreignObject
          className={cn('custom-link-foreign-object', {
            'value-added': isValueAdded,
            'value-reduced': !isValueAdded
          })}
          x={isValueAdded ? sourceX - 275 : sourceX + 165}
          y={isValueAdded ? sourceY - linkWidth / 2 : targetY - linkWidth / 2}
          height={linkWidth}
        >
          <ConditionalTooltip tooltipChildren={isPrimary ? primaryDefinition : undefined}>
            <div
              className={cn('sankey-label-container', {
                'value-added': isValueAdded,
                'value-reduced': !isValueAdded
              })}
            >
              <div
                className={cn('sankey-label', {
                  primary: isPrimary,
                  'value-added': isValueAdded,
                  'value-reduced': !isValueAdded
                })}
              >
                {isPrimary
                  ? `${isValueAdded ? 'Direct' : 'Actualized'} $: `
                  : `${isValueAdded ? payload.source.name : payload.target.name}: `}
                {prettyLabel}
                &nbsp;
              </div>
            </div>
          </ConditionalTooltip>
        </foreignObject>
      </g>
    );
  };

interface ConditionalTooltipProps {
  tooltipChildren: ReactNode;
  placement?: PlacementType;
  children: ReactElement;
}

// Wraps children in a hover tooltip only when there is tooltip content; otherwise renders
// the children directly (mirrors the old TooltipOverlay behavior).
const ConditionalTooltip = ({
  tooltipChildren,
  placement = Placement.AUTO,
  children
}: ConditionalTooltipProps) => {
  if (!tooltipChildren) return children;

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      placement={placement}
      overlay={
        <Popover className='spend-effect-tooltip-popover'>
          <Popover.Body>{tooltipChildren}</Popover.Body>
        </Popover>
      }
    >
      {children}
    </OverlayTrigger>
  );
};

interface LegendItemProps {
  definition: string;
  icon: ReactNode;
  label: string;
}

const LegendItem = ({ definition, icon, label }: LegendItemProps) => (
  <ConditionalTooltip tooltipChildren={definition} placement={Placement.BOTTOM}>
    <Button
      appearance={Appearance.EMPTY}
      design={Design.NEUTRAL}
      size={Size.SM}
      leadingElement={icon}
    >
      {label}
    </Button>
  </ConditionalTooltip>
);

const HorizontalLegend = () => (
  <div className='horizontal-legend'>
    <div className='label'>Legend:</div>
    <LegendItem
      definition={SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS.Actualized}
      icon={<div className='empty-square' />}
      label='Actualized'
    />
    <LegendItem
      definition={SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS.Reduced}
      icon={<ShadedPattern color='darkgray' />}
      label='Reduced'
    />
    <LegendItem
      definition={SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS.Added}
      icon={<ShadedPattern color='white' backgroundColor='darkgray' id='Negdarkgray' />}
      label='Added'
    />
    <div className='vertical-divider' />
    <LegendItem
      definition={SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS['Demand Capture']}
      icon={<DcDgBadge isDemandCapture />}
      label='Demand Capture'
    />
    <LegendItem
      definition={SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS['Demand Generation']}
      icon={<DcDgBadge isDemandCapture={false} />}
      label='Demand Generation'
    />
  </div>
);

interface PatternDefinitionsProps {
  data: SpendEffectBarDatum[];
  colorMap: Record<string, string>;
}

const PatternDefinitions = ({ data, colorMap }: PatternDefinitionsProps) => (
  <div className='pattern-definitions'>
    <ShadedPattern color='grey' visibleTag={false} />
    <ShadedPattern color='white' backgroundColor='grey' id='Neggrey' visibleTag={false} />
    {data.map(item => (
      <div key={`modalShadedPatternDefs${item.name}`}>
        <ShadedPattern color={colorMap[item.name]} visibleTag={false} />
        <ShadedPattern
          color='white'
          backgroundColor={colorMap[item.name]}
          id={`Neg${colorMap[item.name]}`}
          visibleTag={false}
        />
      </div>
    ))}
  </div>
);

interface ActualizedDirectHeaderProps {
  displayActualValues: boolean;
}

const ActualizedDirectHeader = ({ displayActualValues }: ActualizedDirectHeaderProps) => (
  <div
    className={cn('spend-and-effect-bar-chart-header', {
      'hide-actual-values': !displayActualValues,
      'show-actual-values': displayActualValues
    })}
  >
    <ConditionalTooltip
      tooltipChildren={SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS.Actualized}
      placement={mapBottomPlacement('end')}
    >
      <div className='label'>Actualized</div>
    </ConditionalTooltip>
    <div className='vertical-divider' />
    <ConditionalTooltip
      tooltipChildren={SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS.Direct}
      placement={mapBottomPlacement('start')}
    >
      <div className='label'>Direct</div>
    </ConditionalTooltip>
  </div>
);

interface SingleSankeyProps {
  sankeyData: SankeyData;
  name: 'Spend' | 'Effect';
  isValueAdded: boolean;
  prettyLabel: string;
  node: (props: SankeyNodeRenderProps) => ReactElement;
  link: (props: SankeyLinkRenderProps) => ReactElement<SVGProps<SVGPathElement>>;
}

const SingleSankey = ({
  sankeyData,
  name,
  isValueAdded,
  prettyLabel,
  node,
  link
}: SingleSankeyProps) => {
  const lowerName = name.toLowerCase();

  if (!sankeyData.links.length) {
    return <div className='zero-data-message'>Zero {lowerName} for this channel</div>;
  }

  const zeroPrimaryText = () => {
    if (!sankeyData.zeroPrimaryValue) return '';
    if (isValueAdded) return `No direct ${lowerName}`;
    return `Actualized ${name} reduced to zero`;
  };

  return (
    <>
      {name === 'Effect' ? <div className='effect-title'>Effect: </div> : <div>Spend: </div>}
      <ConditionalTooltip
        tooltipChildren={SPEND_AND_EFFECT_GLOSSARY_DEFINITIONS[`Actualized ${name}`]}
      >
        <div className={cn('total-sankey-label', { added: isValueAdded })}>
          <div className='total-sankey-label-inner'>{prettyLabel}</div>
          <div className='zero-primary-value'>{zeroPrimaryText()}</div>
        </div>
      </ConditionalTooltip>
      <div className={cn('sankey-chart', { effect: name === 'Effect', added: isValueAdded })}>
        <Sankey
          height={sankeyData.links.length > 5 ? 280 : 180}
          width={170}
          data={sankeyData}
          node={node}
          link={link}
          nodePadding={19}
        />
      </div>
    </>
  );
};

interface BreakdownTitleProps {
  activeChannel: string;
  selectedBreakdown: string;
  setSelectedBreakdown: (value: string) => void;
}

const BreakdownTitle = ({
  activeChannel,
  selectedBreakdown,
  setSelectedBreakdown
}: BreakdownTitleProps): ReactNode => {
  if (!DEFAULT_SORT_OPTIONS.includes(activeChannel)) {
    return (
      <div className='title-with-dropdown'>
        <span>Breakdown by </span>
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          size={Size.LG}
          options={[FULL_BREAKDOWN, DOLLAR_BREAKDOWN, ACTUAL_VALUES]}
          value={selectedBreakdown}
          onChange={setSelectedBreakdown}
        />
      </div>
    );
  }
  if (activeChannel === DEMAND_GEN_VS_CAP) {
    return 'Select a single channel from the dropdown to see channel interactions';
  }
  return 'Select a channel to see breakdown';
};

export const SpendAndEffectModal = ({
  onClose,
  data,
  valueFormatter,
  sortOptions,
  kpiType,
  excelDownload,
  groupBy,
  setGroupBy,
  groupByOptions,
  sortDropdownValue,
  setSortDropdownValue,
  modelDate
}: SpendAndEffectModalProps) => {
  const [displayActualValues, setDisplayActualValues] = useState<boolean>(false);
  const [activeChannel, setActiveChannel] = useState<string>(ALL_CHANNELS);
  const [barData, setBarData] = useState<SpendEffectBarDatum[]>(
    spendAndEffectDataSort(data, sortDropdownValue)
  );
  const [xAxisScale, setXAxisScale] = useState<number>(0);
  const [selectedBreakdown, setSelectedBreakdown] = useState<string>(FULL_BREAKDOWN);
  const [showGlossary, setShowGlossary] = useState(false);

  const groupByDisabled = useMemo(
    () => lessThanIgnoreTime(modelDate, GROUP_BY_SUPPORT_DATE_STR),
    [modelDate]
  );

  useEffect(() => {
    setBarData(spendAndEffectDataSort(data, sortDropdownValue));
  }, [data, sortDropdownValue]);

  const activeChannelData = useMemo(
    () =>
      DEFAULT_SORT_OPTIONS.includes(activeChannel)
        ? undefined
        : data.find(item => item.name === activeChannel),
    [data, activeChannel]
  );

  const isValueAdded = activeChannelData?.added || false;

  const activeChannelMap = useMemo(
    () =>
      data.reduce(
        (acc, channel) => {
          acc[ALL_CHANNELS].add(channel.name);
          acc[DEMAND_GEN_VS_CAP].add(channel.name);
          if (channel.added) {
            acc[DEMAND_GEN].add(channel.name);
          } else {
            acc[DEMAND_CAP].add(channel.name);
          }
          acc[channel.name] = channel.contributors;
          return acc;
        },
        {
          [ALL_CHANNELS]: new Set<string>(),
          [DEMAND_GEN]: new Set<string>(),
          [DEMAND_CAP]: new Set<string>(),
          [DEMAND_GEN_VS_CAP]: new Set<string>()
        }
      ),
    [data]
  );

  const demandGenBarData = useMemo(
    () => getBarChartContributorsData(barData, activeChannelMap[DEMAND_GEN]),
    [activeChannelMap, barData]
  );

  const demandCapBarData = useMemo(
    () => getBarChartContributorsData(barData, activeChannelMap[DEMAND_CAP]),
    [activeChannelMap, barData]
  );

  const combinedDemandGenBarData = useMemo(
    () => buildCombinedBarData(demandGenBarData, COMBINED_DEMAND_GEN_NAME, true),
    [demandGenBarData]
  );

  const combinedDemandCapBarData = useMemo(
    () => buildCombinedBarData(demandCapBarData, COMBINED_DEMAND_CAP_NAME, false),
    [demandCapBarData]
  );

  const [sankeyPrimaryPrettyLabelSpend, sankeyPrimaryPrettyLabelEffect] = useMemo(() => {
    const keyWord = isValueAdded ? 'Actualized' : 'Direct';
    if (selectedBreakdown !== ACTUAL_VALUES) {
      return [`${selectedBreakdown} ${keyWord} Spend`, `${selectedBreakdown} ${keyWord} Effect`];
    }
    const spend = isValueAdded
      ? activeChannelData?.totalActualizedSpendRawValueLabel
      : activeChannelData?.totalSpendForChannelLabel;
    const effect = isValueAdded
      ? activeChannelData?.totalActualizedEffectRawValueLabel
      : activeChannelData?.totalEffectForChannelLabel;
    return [`${spend} ${keyWord} Spend`, `${effect} ${keyWord} Effect`];
  }, [isValueAdded, selectedBreakdown, activeChannelData]);

  const [spendSankeyData, effectSankeyData] = useMemo(() => {
    const activeChannelSpendTotal =
      (activeChannelData?.contributorsSpendSum ?? 0) + (activeChannelData?.value ?? 0);
    const activeChannelEffectTotal =
      (activeChannelData?.contributorsEffectSum ?? 0) + (activeChannelData?.value2 ?? 0);

    const startingLink = isValueAdded
      ? { source: 1, target: 0, value: 0 }
      : { source: 0, target: 1, value: 0 };
    const initialSpendObj: SankeyData = {
      nodes: [{ name: 'activeChannelSpend' }, { name: 'activeChannelSpend2' }],
      links: [{ ...startingLink }],
      zeroPrimaryValue: false
    };
    const initialEffectObj: SankeyData = {
      nodes: [{ name: 'activeChannelEffect' }, { name: 'activeChannelEffect2' }],
      links: [{ ...startingLink }],
      zeroPrimaryValue: false
    };
    if (DEFAULT_SORT_OPTIONS.includes(activeChannel)) {
      return [initialSpendObj, initialEffectObj];
    }

    const spend = initialSpendObj;
    const effect = initialEffectObj;

    data.forEach(cv => {
      if (activeChannel === cv.name) {
        if (selectedBreakdown === ACTUAL_VALUES) {
          if (cv.spendRawValue) {
            spend.links[0].value = cv.spendRawValue;
          } else {
            spend.zeroPrimaryValue = true;
            spend.links.shift();
          }
          if (cv.effectRawValue) {
            effect.links[0].value = cv.effectRawValue;
          } else {
            effect.zeroPrimaryValue = true;
            effect.links.shift();
          }
        } else {
          if (cv.value) {
            spend.links[0].value = (cv.value / activeChannelSpendTotal) * 100;
          } else {
            spend.zeroPrimaryValue = true;
            spend.links.shift();
          }
          if (cv.value2) {
            effect.links[0].value = (cv.value2 / activeChannelEffectTotal) * 100;
          } else {
            effect.zeroPrimaryValue = true;
            effect.links.shift();
          }
        }
      }
      if (activeChannelMap[activeChannel].has(cv.name)) {
        if (selectedBreakdown === ACTUAL_VALUES) {
          if (cv[`${activeChannel}:spendRawValue`]) {
            spend.nodes.push({ name: cv.name });
            spend.links.push({
              target: isValueAdded ? 0 : spend.nodes.length - 1,
              source: isValueAdded ? spend.nodes.length - 1 : 0,
              value: cv[`${activeChannel}:spendRawValue`]
            });
          }

          if (cv[`${activeChannel}:effectRawValue`]) {
            effect.nodes.push({ name: cv.name });
            effect.links.push({
              target: isValueAdded ? 0 : effect.nodes.length - 1,
              source: isValueAdded ? effect.nodes.length - 1 : 0,
              value: cv[`${activeChannel}:effectRawValue`]
            });
          }
        } else {
          if (cv[`${activeChannel}:spend`]) {
            spend.nodes.push({ name: cv.name });
            spend.links.push({
              target: isValueAdded ? 0 : spend.nodes.length - 1,
              source: isValueAdded ? spend.nodes.length - 1 : 0,
              value: (cv[`${activeChannel}:spend`] / activeChannelSpendTotal) * 100
            });
          }
          if (cv[`${activeChannel}:effect`]) {
            effect.nodes.push({ name: cv.name });
            effect.links.push({
              target: isValueAdded ? 0 : effect.nodes.length - 1,
              source: isValueAdded ? effect.nodes.length - 1 : 0,
              value: (cv[`${activeChannel}:effect`] / activeChannelEffectTotal) * 100
            });
          }
        }
      }
    });

    return [spend, effect];
  }, [data, activeChannel, activeChannelMap, isValueAdded, activeChannelData, selectedBreakdown]);

  const colorMap = useMemo(() => {
    if (data) {
      return {
        ...getColorMap(data),
        [COMBINED_DEMAND_GEN_NAME]: Neutral400,
        [COMBINED_DEMAND_CAP_NAME]: Neutral300
      };
    }
    return {};
  }, [data]);

  const sankeyContext: SankeyRenderContext = { isValueAdded, colorMap, activeChannel, kpiType };
  const sankeyNode = renderSankeyNode(sankeyContext);

  const channelOptions = useMemo(
    () => [
      ...DEFAULT_SORT_OPTIONS.map(option => ({ value: option, section: DropdownSection.DEFAULTS })),
      ...data.map(channel => ({ value: channel.name, section: DropdownSection.CHANNELS }))
    ],
    [data]
  );

  const returnToAllChannelsView = () => {
    setBarData(
      spendAndEffectDataSort(
        getBarChartContributorsData(data, activeChannelMap[ALL_CHANNELS]),
        sortDropdownValue
      )
    );
    setActiveChannel(ALL_CHANNELS);
    setSelectedBreakdown(FULL_BREAKDOWN);
  };

  const topBarData = useMemo(() => {
    switch (activeChannel) {
      case ALL_CHANNELS:
        return demandGenBarData;
      case DEMAND_GEN_VS_CAP:
        return combinedDemandGenBarData;
      default:
        return data.filter(el => el.name === activeChannel);
    }
  }, [activeChannel, combinedDemandGenBarData, data, demandGenBarData]);

  const bottomBarData = useMemo(() => {
    switch (activeChannel) {
      case ALL_CHANNELS:
        return demandCapBarData;
      case DEMAND_GEN_VS_CAP:
        return combinedDemandCapBarData;
      default:
        return barData;
    }
  }, [activeChannel, barData, combinedDemandCapBarData, demandCapBarData]);

  const showBreakdown = !DEFAULT_SORT_OPTIONS.includes(activeChannel);

  return (
    <div>
      <SpendAndEffectGlossary show={showGlossary} setShow={setShowGlossary} />
      <Modal show onHide={onClose} size='xl' className='spend-and-effect-modal'>
        <WidgetContainer
          header={<div>Spend and Effect Share Details</div>}
          rightActions={
            <Modal.Header closeButton className='spend-effect-modal-header'>
              <ToggleSwitch
                design={Design.BRAND}
                size={Size.SM}
                label={ACTUAL_VALUES}
                checked={displayActualValues}
                onChange={setDisplayActualValues}
              />
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                size={Size.SM}
                value={sortDropdownValue}
                onChange={value => {
                  setBarData(d => spendAndEffectDataSort(d, value));
                  setSortDropdownValue(value);
                }}
                options={sortOptions}
              />
              <ConditionalTooltip
                tooltipChildren={
                  groupByDisabled
                    ? 'Models created before March 21st 2025 cannot support group by functionality'
                    : undefined
                }
                placement={mapBottomPlacement('center')}
              >
                <div>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    className='mmm-group-by-dropdown'
                    label='Group by'
                    size={Size.SM}
                    onChange={setGroupBy}
                    options={groupByOptions}
                    value={groupBy}
                    disabled={groupByDisabled}
                  />
                </div>
              </ConditionalTooltip>
              <Button
                size={Size.SM}
                trailingElement={<MdOutlineMenuBook />}
                onClick={() => setShowGlossary(true)}
              >
                Glossary
              </Button>
              <DownloadDropdown
                size={Size.SM}
                onClickOptions={[excelDownload]}
                menuOptions={['XLSX']}
              />
            </Modal.Header>
          }
          footer={
            <div className='spend-and-effect-modal-bottom-wrapper'>
              <IncrementalityIcon className='incrementality-icon-bottom' />
              <div>
                Channels marked with a green beaker use informative priors based on incrementality
                studies. Incrementality testing is strongly recommended, and we encourage you to
                contact your team to review options.
              </div>
            </div>
          }
        >
          <Modal.Body className='spend-effect-modal-body'>
            <ChartContainer
              viewRightActions
              title={
                <>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    type={DropdownToggleType.WIDGET_TITLE}
                    value={activeChannel}
                    options={channelOptions}
                    section={[
                      { title: DropdownSection.DEFAULTS, showSectionTitle: false },
                      { title: DropdownSection.CHANNELS, showSectionTitle: false }
                    ]}
                    onChange={option => {
                      setBarData(getBarChartContributorsData(data, activeChannelMap[option]));
                      setActiveChannel(option);
                    }}
                  />
                  {showBreakdown && (
                    <Button
                      appearance={Appearance.OUTLINED}
                      size={Size.SM}
                      design={Design.NEUTRAL}
                      leadingElement={<TiArrowBack color={Brand60} />}
                      onClick={returnToAllChannelsView}
                    >
                      Back
                    </Button>
                  )}
                </>
              }
              rightActions={
                <div className='group-right-actions'>
                  <HorizontalLegend />
                </div>
              }
            >
              <div className='multi-bar-chart-container'>
                <PatternDefinitions data={data} colorMap={colorMap} />
                <ActualizedDirectHeader displayActualValues={displayActualValues} />
                {![DEMAND_CAP, DEMAND_GEN].includes(activeChannel) && (
                  <>
                    <SpendAndEffectBarChart
                      expandedModal
                      activeChannel={activeChannel}
                      setActiveChannel={setActiveChannel}
                      barData={topBarData}
                      setBarData={setBarData}
                      data={data}
                      displayActualValues={displayActualValues}
                      valueFormatter={valueFormatter}
                      className='active-channel'
                      colorMap={colorMap}
                      barSize={24}
                      key='SpendandEffectMMMModalActiveChannel'
                      xAxisScale={xAxisScale}
                      setXAxisScale={setXAxisScale}
                      groupBy={groupBy}
                    />
                    <div className='horizontal-divider' />
                  </>
                )}
                <SpendAndEffectBarChart
                  expandedModal
                  activeChannel={activeChannel}
                  setActiveChannel={setActiveChannel}
                  barData={bottomBarData}
                  setBarData={setBarData}
                  data={data}
                  displayActualValues={displayActualValues}
                  valueFormatter={valueFormatter}
                  colorMap={colorMap}
                  barSize={24}
                  key='SpendandEffectMMMModalAllChannels'
                  xAxisScale={xAxisScale}
                  setXAxisScale={setXAxisScale}
                  groupBy={groupBy}
                />
                <InteractiveDiv
                  className='multi-bar-chart-container-background'
                  disabled={!showBreakdown}
                  onClick={showBreakdown ? returnToAllChannelsView : undefined}
                />
              </div>
              <div className={cn('secondary-chart', { 'breakdown-available': showBreakdown })}>
                <ChartContainer
                  title={
                    <BreakdownTitle
                      activeChannel={activeChannel}
                      selectedBreakdown={selectedBreakdown}
                      setSelectedBreakdown={setSelectedBreakdown}
                    />
                  }
                >
                  {showBreakdown && (
                    <div className='sankey-container'>
                      <div className='single-sankey-container'>
                        <SingleSankey
                          sankeyData={spendSankeyData}
                          name='Spend'
                          isValueAdded={isValueAdded}
                          prettyLabel={sankeyPrimaryPrettyLabelSpend}
                          node={sankeyNode}
                          link={renderSankeyLink(sankeyContext, 'Spend', selectedBreakdown)}
                        />
                      </div>
                      <div className='single-sankey-container'>
                        <SingleSankey
                          sankeyData={effectSankeyData}
                          name='Effect'
                          isValueAdded={isValueAdded}
                          prettyLabel={sankeyPrimaryPrettyLabelEffect}
                          node={sankeyNode}
                          link={renderSankeyLink(sankeyContext, 'Effect', selectedBreakdown)}
                        />
                      </div>
                    </div>
                  )}
                </ChartContainer>
              </div>
            </ChartContainer>
          </Modal.Body>
        </WidgetContainer>
      </Modal>
    </div>
  );
};
